import React, { useState, useEffect, useCallback, useRef } from 'react';
import useResizableSplitPane, { getLeaseAbstractionSplitStorageKey, getSplitPaneWidthCssValue } from '../useResizableSplitPane';
import { Link, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { RadioGroup, RadioButton, Icon, Tooltip, Button, Checkbox } from '@cbrebuild/blocks';
import Datepicker from '../../../nucleus/datepicker/datepicker';
import useVoucherValidation from '../useVoucherValidation';
import NumberInput from '../../../nucleus/inputs/number-input';
import AddressForm from '../AddressForm';
import Select from '../../../nucleus/select/select';
import useCommercialPropertyState from '../../../hooks/useCommercialPropertyState';
import useCompanyState from '../../../hooks/useCompanyState';
import PropertyAutocomplete from '../../autocomplete/property-autocomplete';
import vouchersService from '../../../services/voucher/vouchers-service';
import dealsService from '../../../services/deals-service';
import LeasePdfViewerPanel from './LeasePdfViewerPanel';
import { createLinks } from '../BottomNav';
import { subscribeToDealExtractionStatus } from '../../../services/attachments-service';
import contactsService from '../../../services/contacts-service';
import companiesService, { defaultCompanyData } from '../../../services/companies-service';
import LeaseDetails from '../../file-upload/lease-details/lease-details';
import LeaseAbstractionContext from '../../file-upload/contexts/leaseAbstractionContext';
import useFileAbstractionFields from '../../../hooks/useFileAbstractionFields';
import { validatePayload } from '../../../utils/validation-utils';
import {
  LEASE_ABSTRACTION_VALIDATION_PAGES,
  isLeaseAbstractionPageValidated,
  setLeaseAbstractionPageValidated,
} from '../../../utils/lease-abstraction-validation-storage';
import { PARENT_KEYS, TRACKING_IDS } from '../../../constants/lease-abstarction';
import {
  LEASE_EXTRACTION_REFRESH_EVENT,
  dispatchLeaseExtractionRefreshRequest,
} from '../../../hooks/useLeaseExtractionPersistedDataRefresh';
import validationSchema from '../../file-upload/validation-schema';
import countries from '../../../data/countries.json';
import states from '../../../data/states.json';

const LEASE_DETAILS_SECTION_ROUTE_MAP = {
  leaseInformation: 'leaseInformation',
  tenantInformation: 'tenantInformation',
  landlordInformation: 'landlordInformation',
};

const getTargetLeaseDetailsSection = (search) => {
  const params = new URLSearchParams(search || '');
  const section = params.get('section');
  return LEASE_DETAILS_SECTION_ROUTE_MAP[section] || null;
};

export const shouldSyncCompanySnapshot = (syncState, companyId, syncRevision) => {
  if (!companyId) return false;
  return syncState.id !== companyId || syncState.syncRevision !== syncRevision;
};

const LeaseInformation = ({
  deal,
  voucher,
  updateDeal,
  updateVoucher,
  abstractionLeaseFile,
  dealFiles,
  fetchFiles,
  fetchFinancials,
  uploadFile,
}) => {
  const [extractionResults, setExtractionResults] = useState([]);
  const [allExtractionStatusMap, setAllExtractionStatusMap] = useState({});
  const [overrideFile, setOverrideFile] = useState(null);
  const [showMoreInstructions, setShowMoreInstructions] = useState(false);
  const [persistedDataRefreshToken, setPersistedDataRefreshToken] = useState(0);
  const requestedPersistedRefreshRef = useRef(null);
  const suppressPersistedRefreshForFileRef = useRef(null);
  const loadedFinancialCommentsRef = useRef(false);
  const createdCompaniesRef = useRef({});
  const companyCreateRequestsRef = useRef({});
  const tenantCompanySyncRef = useRef({ id: null, syncRevision: null });
  const landlordCompanySyncRef = useRef({ id: null, syncRevision: null });
  const [loadedSectionsState, setLoadedSectionsState] = useState({
    dealId: deal.id,
    sections: {},
  });
  const {
    containerRef,
    leftPaneWidth,
    minLeftWidth,
    maxLeftWidth,
    startResize,
    handleDividerKeyDown,
    dividerHintText,
  } = useResizableSplitPane({ storageKey: getLeaseAbstractionSplitStorageKey(deal.id) });

  // Fetch files on mount if not already loaded (e.g. navigated directly to this tab)
  useEffect(() => {
    if (!dealFiles || dealFiles.length === 0) {
      fetchFiles({
        deal: deal.id,
        ordering: '-modified',
        page: 1,
        page_size: 25,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  useEffect(() => {
    loadedFinancialCommentsRef.current = false;
    createdCompaniesRef.current = {};
    companyCreateRequestsRef.current = {};
    tenantCompanySyncRef.current = { id: null, syncRevision: null };
    landlordCompanySyncRef.current = { id: null, syncRevision: null };
  }, [deal.id]);

  const loadSection = useCallback((sectionKey) => {
    setLoadedSectionsState((current) => {
      const currentSections = current.dealId === deal.id ? current.sections : {};
      if (currentSections[sectionKey] && current.dealId === deal.id) return current;
      return {
        dealId: deal.id,
        sections: {
          ...currentSections,
          [sectionKey]: true,
        },
      };
    });
  }, [deal.id]);

  const loadedSections = loadedSectionsState.dealId === deal.id ? loadedSectionsState.sections : {};

  const isLeaseInformationLoaded = !!loadedSections.leaseInformation;
  const isTenantInformationLoaded = !!loadedSections.tenantInformation;
  const isLandlordInformationLoaded = !!loadedSections.landlordInformation;

  // Resolve the active lease file:
  // 1. Redux abstractionLeaseFile (set when user explicitly imports via modal)
  // 2. localStorage fallback (persists across page refreshes)
  // 3. First extraction-opted-in lease file found in the deal files list
  // Only use a remembered/stored file if it still exists in the current dealFiles list.
  const fileIds = new Set((dealFiles || []).map(f => f.id));

  const resolvedLeaseFile = (() => {
    if (abstractionLeaseFile && abstractionLeaseFile.id) {
      // If the remembered Redux file was deleted, ignore it
      if (fileIds.size > 0 && !fileIds.has(abstractionLeaseFile.id)) return null;
      if (fileIds.size > 0) return abstractionLeaseFile;
    }
    try {
      const stored = localStorage.getItem(`abstractionVoucherFile_${deal.id}`);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed && parsed.id) {
          // Only remove if files are loaded AND the file is confirmed gone.
          // While fileIds is empty (files not yet fetched) keep the stored entry.
          if (fileIds.size > 0 && !fileIds.has(parsed.id)) {
            localStorage.removeItem(`abstractionVoucherFile_${deal.id}`);
          } else {
            return parsed;
          }
        }
      }
    } catch (e) { /* ignore */ }
    if (dealFiles && dealFiles.length > 0) {
      const leaseFile = dealFiles.find(f => f.is_lease_document === true);
      if (leaseFile) return leaseFile;
      return dealFiles[0];
    }
    return null;
  })();

  // If overrideFile was deleted, clear it
  const activeFile = (overrideFile && fileIds.has(overrideFile.id)) ? overrideFile : resolvedLeaseFile;
  const activeFileId = activeFile && activeFile.id;

  // Subscribe to shared deal-level extraction status updates for the active file.
  useEffect(() => {
    if (!(activeFile && activeFile.id)) {
      setExtractionResults([]);
      return undefined;
    }

    return subscribeToDealExtractionStatus({
      dealId: deal.id,
      fileId: activeFile.id,
      onUpdate: (result, allResults) => {
        setExtractionResults(result ? [result] : []);
        if (Array.isArray(allResults)) {
          const map = {};
          allResults.forEach((r) => {
            const fid = r.file || r.file_id || r.fileId || r.attachment_file_id;
            if (fid != null) map[String(fid)] = r.status;
          });
          setAllExtractionStatusMap(map);
        }
      },
      onError: () => setExtractionResults([]),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFile && activeFile.id, deal.id]);

  const [leaseStartDate, setLeaseStartDate] = useState('');
  const [leaseEndDate, setLeaseEndDate] = useState('');
  const [leaseExecutionDate, setLeaseExecutionDate] = useState('');
  const [leaseOccupancyDate, setLeaseOccupancyDate] = useState('');
  const [agreementType, setAgreementType] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [agreementErrorDrpDwn, setAgreementErrorDrpDwn] = useState(false);
  const [linkedProperties, setLinkedProperties] = useState({});
  const [leaseErrorDrpDwn, setLeaseErrorDrpDwn] = useState(false);
  const [showMore, setShowMore] = useState({});
  const [, setAgileDeal] = useState('');
  const [, setLeaseDeal] = useState('');
  const [costPerSeat, setCostPerSeat] = useState('');
  const [propertyOptions, setPropertyOptions] = useState([{ display_name: 'None', value: null }]);
  const [noOfSeats, setNoOfSeats] = useState('');
  const {
    properties, dealProperties, onPropertyChange, onPropertyBlur, onSpaceBlur, onSpaceChange, onCurrencyBlur, onDealPropertySelect, onDealPropertyAdd,
  } = useCommercialPropertyState({
    dealId: deal.id,
    createIfEmpty: true,
    refreshToken: persistedDataRefreshToken,
    enabled: isLeaseInformationLoaded,
  });

  // Tenant company state — used by Tenant Information accordion
  const tenantPartyType = (deal.rep_role === 'landlord' || deal.rep_role === 'seller') ? 'counter_party' : 'client';
  const {
    company, onCompanyChange, onCompanyBlur,
    searchState: tenantSearchState, onSuggestionSelect: onTenantSuggestionSelect, closeSuggestions: closeTenantSuggestions,
    triggerSearch: triggerTenantSearch,
  } = useCompanyState(
    deal[tenantPartyType] || undefined,
    tenantPartyType,
    update => updateDeal(deal.id, update),
    persistedDataRefreshToken,
    isTenantInformationLoaded,
    { duplicateOnNameBlur: false },
  );

  // Landlord company state — used by Landlord Information accordion
  const landlordPartyType = (deal.rep_role === 'landlord' || deal.rep_role === 'seller') ? 'client' : 'counter_party';
  const {
    company: landlordCompany, onCompanyChange: onLandlordCompanyChange, onCompanyBlur: onLandlordCompanyBlur,
    searchState: landlordSearchState, onSuggestionSelect: onLandlordSuggestionSelect, closeSuggestions: closeLandlordSuggestions,
    triggerSearch: triggerLandlordSearch,
  } = useCompanyState(
    deal[landlordPartyType] || undefined,
    landlordPartyType,
    update => updateDeal(deal.id, update),
    persistedDataRefreshToken,
    isLandlordInformationLoaded,
    { duplicateOnNameBlur: false },
  );

  // Tenant / landlord contact IDs — fetched on mount for chip accept-to-DB
  const [tenantContactId, setTenantContactId] = useState(null);
  const [landlordContactId, setLandlordContactId] = useState(null);
  const [tenantContact, setTenantContact] = useState(null);
  const [landlordContact, setLandlordContact] = useState(null);
  useEffect(() => {
    if (!isTenantInformationLoaded && !isLandlordInformationLoaded) return;
    contactsService.getContacts({
      deal: deal.id,
      ordering: '-is_primary',
      page: 1,
      page_size: 25,
    }).then(async (res) => {
      if (res && res.results) {
        let tc = res.results.find(c => c.contact_type === 'tenant');
        let lc = res.results.find(c => c.contact_type === 'landlord');
        if (isTenantInformationLoaded && !tc) {
          try {
            tc = await contactsService.postContact({
              contact_type: 'tenant',
              create_personal_contact: true,
              deal: deal.id,
              email_addresses: [],
              given_name: '',
              is_primary: false,
              phone_numbers: [],
              surname: '',
            });
          } catch (e) { /* ignore — patch will be silently skipped */ }
        }
        if (isLandlordInformationLoaded && !lc) {
          try {
            lc = await contactsService.postContact({
              contact_type: 'landlord',
              create_personal_contact: true,
              deal: deal.id,
              email_addresses: [],
              given_name: '',
              is_primary: false,
              phone_numbers: [],
              surname: '',
            });
          } catch (e) { /* ignore */ }
        }
        if (tc) { setTenantContactId(tc.id); setTenantContact(tc); }
        if (lc) { setLandlordContactId(lc.id); setLandlordContact(lc); }
      }
    }).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id, persistedDataRefreshToken, isTenantInformationLoaded, isLandlordInformationLoaded]);

  useEffect(() => {
    const handlePersistedDataRefresh = (event) => {
      const detail = event?.detail || {};
      if (detail.dealId !== deal.id) return;
      if (activeFileId && detail.fileId && detail.fileId !== activeFileId) return;
      setPersistedDataRefreshToken(token => token + 1);
    };

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener(LEASE_EXTRACTION_REFRESH_EVENT, handlePersistedDataRefresh);
    }

    return () => {
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener(LEASE_EXTRACTION_REFRESH_EVENT, handlePersistedDataRefresh);
      }
    };
  }, [activeFileId, deal.id]);

  const extractionResult = extractionResults[0];
  const extractionStatus = extractionResult?.status;

  useEffect(() => {
    if (extractionStatus !== 'Success' || !activeFileId) return;
    const refreshKey = `${activeFileId}:${extractionResult?.id || extractionResult?.status_text || extractionStatus}`;
    if (requestedPersistedRefreshRef.current === refreshKey) return;
    if (suppressPersistedRefreshForFileRef.current === activeFileId) {
      requestedPersistedRefreshRef.current = refreshKey;
      return;
    }
    requestedPersistedRefreshRef.current = refreshKey;
    dispatchLeaseExtractionRefreshRequest({
      dealId: deal.id,
      fileId: activeFileId,
      refreshKey,
      extractionResult,
    });
  }, [activeFileId, deal.id, extractionResult, extractionStatus]);

  // Direct-patch helpers exposed to context for chip accept handlers
  const createOrPatchCompany = (currentCompany, patch, partyType) => {
    const savedCompany = (currentCompany && currentCompany.id) ? currentCompany : createdCompaniesRef.current[partyType];
    if (savedCompany && savedCompany.id) {
      const patchData = companiesService.preparePatchData(patch, savedCompany);
      return companiesService.patchCompany(savedCompany.id, patchData);
    }

    const pendingCreate = companyCreateRequestsRef.current[partyType];
    if (pendingCreate) {
      return pendingCreate.then((createdCompany) => {
        if (!createdCompany || !createdCompany.id) return null;
        const patchData = companiesService.preparePatchData(patch, createdCompany);
        return companiesService.patchCompany(createdCompany.id, patchData);
      });
    }

    const companyDraft = { ...(currentCompany || {}) };
    delete companyDraft.id;
    delete companyDraft.parent_entity_name;
    delete companyDraft.parent_trips_id;
    const createPromise = companiesService.createCompany({
      ...defaultCompanyData,
      ...companyDraft,
      ...patch,
    }).then((createdCompany) => {
      if (createdCompany && createdCompany.id) {
        createdCompaniesRef.current[partyType] = createdCompany;
        updateDeal(deal.id, { [partyType]: createdCompany.id });
      }
      return createdCompany;
    }).finally(() => {
      if (companyCreateRequestsRef.current[partyType] === createPromise) {
        delete companyCreateRequestsRef.current[partyType];
      }
    });

    companyCreateRequestsRef.current[partyType] = createPromise;
    return createPromise;
  };

  const patchTenantCompany = patch => createOrPatchCompany(company, patch, tenantPartyType);
  const patchLandlordCompany = patch => createOrPatchCompany(landlordCompany, patch, landlordPartyType);
  const patchTenantContact = (patch) => {
    if (tenantContactId) contactsService.patchContact(tenantContactId, patch);
  };
  const patchLandlordContact = (patch) => {
    if (landlordContactId) contactsService.patchContact(landlordContactId, patch);
  };

  // Prior location state — tenant relocation section
  const {
    properties: priorProperties,
    onPropertyChange: onPriorPropertyChange,
    onPropertyBlur: onPriorPropertyBlur,
    onPropertyCreate: onPriorPropertyCreate,
    onPropertyDelete: onPriorPropertyDelete,
    onSpaceChange: onPriorSpaceChange,
    onSpaceBlur: onPriorSpaceBlur,
  } = useCommercialPropertyState({
    dealId: deal.id,
    filterPropertyResultsBy: p => p.location_type_new === false,
    enabled: isTenantInformationLoaded,
  });
  const priorProperty = priorProperties && priorProperties.length > 0 ? priorProperties[0] : null;
  // Derive relocating from actual fetched data — the hook's isTenantRelocating
  // is only updated inside onPropertyCreate (not on fetch), so it's unreliable.
  const isTenantRelocating = priorProperties != null && priorProperties.length > 0;
  const PAGE_NUMBER = 2.1;

  // uses the useValidation hook to see if this page should show all validation
  const { showPageValidation } = useVoucherValidation(voucher.id, PAGE_NUMBER);

  // ─── Abstraction mode: state & hooks (always called, used when activeFile present) ───
  const [modalFieldState, dispatchModalFieldState] = useFileAbstractionFields();
  const [responseState, dispatchResponseState] = useFileAbstractionFields();
  const [fieldErrorMap, setFieldErrorMap] = useState({});
  const [tAndCLeaseDetails, setTAndCLeaseDetails] = useState(() => isLeaseAbstractionPageValidated(
    deal.id,
    LEASE_ABSTRACTION_VALIDATION_PAGES.leaseDetails,
  ));
  const [pdfPageData, setPdfPageData] = useState({ pageNumber: 1, annotationField: 'N/A' });
  const [currentHighlightedField, setCurrentHighlightedField] = useState('N/A');

  const [agreementTypeData, setAgreementTypeData] = useState([{ value: '', display_name: 'Select' }]);
  const [spaceTypeData, setSpaceTypeData] = useState([{ value: '', display_name: 'Select' }]);
  const [transactionTypeData, setTransactionTypeData] = useState([{ value: '', display_name: 'Select' }]);

  useEffect(() => {
    setTAndCLeaseDetails(isLeaseAbstractionPageValidated(
      deal.id,
      LEASE_ABSTRACTION_VALIDATION_PAGES.leaseDetails,
    ));
  }, [deal.id]);

  const handleLeaseDetailsValidationChange = (checked) => {
    setTAndCLeaseDetails(checked);
    setLeaseAbstractionPageValidated(deal.id, LEASE_ABSTRACTION_VALIDATION_PAGES.leaseDetails, checked);
  };

  const loadFinancialCommentsPanel = useCallback(() => {
    if (loadedFinancialCommentsRef.current) return;
    loadedFinancialCommentsRef.current = true;
    fetchFinancials(deal.id, deal.deal_type);
  }, [deal.deal_type, deal.id, fetchFinancials]);

  // Populate Rate Type and Lease Transaction Type from OPTIONS /mbb/deals (same as Angular)
  useEffect(() => {
    dealsService.getDealOptions().then((data) => {
      const agreementChoices = data?.actions?.POST?.lease_agreement_type?.choices || [];
      const transactionChoices = data?.actions?.POST?.lease_income_type?.choices || [];
      if (agreementChoices.length > 0) {
        setAgreementTypeData([{ value: '', display_name: 'Select' }, ...agreementChoices]);
      }
      if (transactionChoices.length > 0) {
        setTransactionTypeData([{ value: '', display_name: 'Select' }, ...transactionChoices]);
      }
    }).catch(() => {});
  }, []);

  const buildingClassData = [
    { value: 'A', display_name: 'A' },
    { value: 'B', display_name: 'B' },
    { value: 'C', display_name: 'C' },
    { value: 'D', display_name: 'D' },
  ];

  const onRemapPropertyEvent = (e, callback) => {
    const { target: { type, name, value } } = e;
    if (name === 'postal') {
      callback({
        ...e,
        target: {
          ...e.target, name: 'postal_code', type, value,
        },
      });
    } else if (name === 'territory') {
      callback({
        ...e,
        target: {
          ...e.target, name: 'state_abbreviation', type, value,
        },
      });
    } else {
      callback(e);
    }
  };

  const handleLinkedProperty = (cpId, dp) => {
    onDealPropertySelect(cpId, dp);
    setLinkedProperties(current => ({
      ...current, [cpId]: dp.id,
    }));
  };

  const handleDateChange = (date, propName) => {
    const dateVal = date !== '' ? date : null;
    const patch = {
      [propName]: dateVal,
    };
    if (propName === 'lease_expiration_date') {
      updateVoucher(voucher.id, patch);
    } else {
      updateDeal(deal.id, patch);
    }
    switch (propName) {
      case 'date_lease_from':
        setLeaseStartDate(dateVal);
        break;
      case 'lease_expiration_date':
        setLeaseEndDate(dateVal);
        break;
      case 'date_lease_execution':
        setLeaseExecutionDate(dateVal);
        break;
      case 'date_lease_occupancy':
        setLeaseOccupancyDate(dateVal);
        break;
      default:
        break;
    }
  };

  const handleAgreementChange = (event) => {
    updateDeal(deal.id, { lease_agreement_type: event.value });
    setAgreementType(event.value);
  };

  const handleLeaseIncomeTypeChange = (event) => {
    updateDeal(deal.id, { lease_income_type: event.value });
    setTransactionType(event.value);
  };

  const handleDealChange = (event) => {
    const radioValue = event.target.value;
    setAgileDeal(radioValue);
    updateDeal(deal.id, { is_agile_deal: radioValue });
  };

  const handleLeaseChange = (event) => {
    const radioValue = event.target.value;
    setLeaseDeal(radioValue);
    updateDeal(deal.id, { is_sublease: radioValue });
  };

  const handleSeatChange = (event) => {
    updateDeal(deal.id, { cost_per_seat: event.target.value });
    setCostPerSeat(event.target.value);
  };

  const handleNumseatChange = (event) => {
    updateDeal(deal.id, { number_of_seats: event.target.value });
    setNoOfSeats(event.target.value);
  };

  const handleSeatChangeBlur = (event) => {
    const enteredVal = event.target.value;
    if (enteredVal !== '') {
      const num = Number(enteredVal).toFixed(2);
      setCostPerSeat(num);
      updateDeal(deal.id, { cost_per_seat: num });
    }
  };

  const handleShowMore = (cpId) => {
    setShowMore(current => ({
      ...current, [cpId]: !current[cpId],
    }));
  };

  const addProperty = (id, property) => {
    onDealPropertyAdd(id, property, property.name);
  };

  useEffect(() => {
    if (deal.lease_agreement_type == null || deal.lease_agreement_type === 'Select' || deal.lease_agreement_type === '') {
      setAgreementErrorDrpDwn(true);
    } else {
      setAgreementErrorDrpDwn(false);
    }
    if (deal.lease_income_type == null || deal.lease_income_type === 'Select' || deal.lease_income_type === '') {
      setLeaseErrorDrpDwn(true);
    } else {
      setLeaseErrorDrpDwn(false);
    }
    if (deal) {
      setLeaseStartDate(deal.date_lease_from);
      setLeaseEndDate(voucher.lease_expiration_date);
      setLeaseExecutionDate(deal.date_lease_execution);
      setLeaseOccupancyDate(deal.date_lease_occupancy);
    }
  }, [
    deal,
    deal.lease_agreement_type,
    deal.lease_income_type,
    voucher.lease_expiration_date,
  ]);

  useEffect(() => {
    vouchersService.getPropertyOptions().then((res) => {
      const propertyTypeChoices = res.actions.POST.voucher_property_type_category.choices || [];
      if (propertyTypeChoices.length > 0) {
        setPropertyOptions(current => (current.length === 1 ? current.concat(propertyTypeChoices) : current));
      }
      const spaceChoices = res.actions?.POST?.space_type?.choices || [];
      if (spaceChoices.length > 0) {
        setSpaceTypeData([{ value: '', display_name: 'Select' }, ...spaceChoices]);
      }
    });
  }, []);

  // ─── Abstraction mode helpers ───
  const getFilteredStatesForAbstraction = (countryName = 'United States') => {
    const found = countries.find(c => c.display_name === countryName);
    return states.filter(s => s.country_id === found?.country_id);
  };

  const modalFieldChangeHandler = useCallback(
    (field, value, type = 'single') => {
      dispatchModalFieldState({ field, value, type });
    },
    [dispatchModalFieldState],
  );

  const setResponseState = useCallback(
    (field, value, type = 'single') => {
      dispatchResponseState({ field, value, type });
    },
    [dispatchResponseState],
  );

  const individualModalFieldChangeHandler = (params) => {
    const { stateVariable, child_value } = params;
    const stateField = responseState[stateVariable];
    if (!stateField) return;
    const normalizedChildValue = String(child_value).trim();
    const normalizedResponseValue = String(stateField.value).trim();
    if (normalizedChildValue === normalizedResponseValue) return;
  };

  const validateLeaseAbstractionFields = () => {
    const { isPayloadValid, errorMap } = validatePayload(validationSchema, modalFieldState);
    setFieldErrorMap(errorMap);
    return {
      isPayloadValid,
      errorMessages: Object.entries(errorMap).map(([f, m]) => `${f}: ${m}`).join(', '),
    };
  };

  // ─── Initialize extraction results into extractedValue (chip display only, not the editable value) ───
  useEffect(() => {
    if (!extractionResults || extractionResults.length === 0) return;
    const results = extractionResults;
    if (!results[0]?.data_extract) return;
    const annotationsData = results[0]?.data_extract?.annotations || {};
    const getFieldConfidence = (basePath, valuePath) => {
      const key = `${basePath}_${valuePath.replace(/\./g, '_')}`;
      const blocks = annotationsData[key];
      if (!blocks || Object.keys(blocks).length === 0) return null;
      const scores = Object.values(blocks)
        .map(b => b.text_confidence)
        .filter(s => typeof s === 'number');
      return scores.length > 0 ? Math.min(...scores) : null;
    };
    // initField sets extractedValue + toolTip (page reference) only.
    // The editable value (value) comes from DB — see the DB init effects below.
    const initField = (fieldName, valuePath, basePath) => {
      const fieldInfo = valuePath.split('.').reduce((acc, part) => acc && acc[part], results[0]?.data_extract?.[basePath]) || '';
      const rawValue = fieldInfo?.value || fieldInfo?.extracted_value || '';
      const fieldInfoValue = {
        extractedValue: rawValue,
        toolTip: {
          section: fieldInfo?.section,
          paragraph: fieldInfo?.paragraph,
          pageIndex: fieldInfo?.page_index ? `${fieldInfo.page_index}*` : fieldInfo?.page_index,
          confidence: getFieldConfidence(basePath, valuePath),
        },
      };
      modalFieldChangeHandler(fieldName, fieldInfoValue, 'single');
      // Keep responseState for backend attachment-props API
      setResponseState(fieldName, { value: rawValue, toolTip: fieldInfoValue.toolTip }, 'single');
    };
    // Lease Info
    initField('propertyName', 'property_name', 'lease_info');
    initField('country', 'country', 'lease_info');
    initField('propertyType', 'property_type_subtype', 'lease_info');
    initField('leaseStartDate', 'lease_start_date', 'lease_info');
    initField('leaseEndDate', 'lease_expiration_date', 'lease_info');
    initField('leaseExecutionDate', 'lease_execution_date', 'lease_info');
    initField('leaseOccupancyDate', 'lease_occupancy_date', 'lease_info');
    initField('address', 'address', 'lease_info');
    initField('city', 'city', 'lease_info');
    initField('stateName', 'state_province', 'lease_info');
    initField('zip', 'zip', 'lease_info');
    initField('isASublease', 'is_sublease', 'lease_info');
    initField('isListedByCBRE', 'is_listed_by_cbre', 'lease_info');
    initField('isWholeBuilding', 'is_whole_building', 'lease_info');
    initField('parcelSize', 'parcel_size', 'lease_info');
    initField('suite', 'suite', 'lease_info');
    initField('floorChange', 'floor_number', 'lease_info');
    initField('squareFeet', 'square_footage', 'lease_info');
    // Tenant Info
    initField('tenantCompanyName', 'org_name', 'tenant_buyer_info');
    initField('tenantCountryName', 'country', 'tenant_buyer_info');
    initField('tenantAddressLine1', 'address', 'tenant_buyer_info');
    initField('tenantAddressLine2', 'address2', 'tenant_buyer_info');
    initField('tenantCity', 'city', 'tenant_buyer_info');
    initField('tenantStateName', 'state_province', 'tenant_buyer_info');
    initField('tenantZip', 'zip', 'tenant_buyer_info');
    initField('tenantPOCPhone', 'contact_phone', 'tenant_buyer_info');
    initField('tenantPOCEmail', 'contact_email', 'tenant_buyer_info');
    initField('tenantPOCFirstName', 'contact_first_name', 'tenant_buyer_info');
    initField('tenantPOCLastName', 'contact_last_name', 'tenant_buyer_info');
    initField('tenantPOCJobTitle', 'contact_title', 'tenant_buyer_info');
    // Landlord Info
    initField('landlordCompanyName', 'org_name', 'landlord_seller_info');
    initField('landlordCountryName', 'country', 'landlord_seller_info');
    initField('landlordAddressLine1', 'address', 'landlord_seller_info');
    initField('landlordAddressLine2', 'address2', 'landlord_seller_info');
    initField('landlordCity', 'city', 'landlord_seller_info');
    initField('landlordStateName', 'state_province', 'landlord_seller_info');
    initField('landlordZip', 'zip', 'landlord_seller_info');
    initField('landlordPOCPhone', 'contact_phone', 'landlord_seller_info');
    initField('landlordPOCEmail', 'contact_email', 'landlord_seller_info');
    initField('landlordPOCFirstName', 'contact_first_name', 'landlord_seller_info');
    initField('landlordPOCLastName', 'contact_last_name', 'landlord_seller_info');
    initField('landlordPOCJobTitle', 'contact_title', 'landlord_seller_info');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractionResults]);

  // ─── Initialize editable values from deal / voucher APIs (DB values) ───
  const leaseExpirationDate = voucher?.lease_expiration_date;

  useEffect(() => {
    if (!deal || !deal.id) return;
    modalFieldChangeHandler('leaseStartDate', { value: deal.date_lease_from || '' });
    modalFieldChangeHandler('leaseEndDate', { value: leaseExpirationDate || '' });
    modalFieldChangeHandler('leaseExecutionDate', { value: deal.date_lease_execution || '' });
    modalFieldChangeHandler('leaseOccupancyDate', { value: deal.date_lease_occupancy || '' });
    modalFieldChangeHandler('isASublease', { value: deal.is_sublease != null ? `${deal.is_sublease}` : '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deal.id,
    deal.date_lease_from,
    deal.date_lease_execution,
    deal.date_lease_occupancy,
    deal.is_sublease,
    leaseExpirationDate,
  ]);

  // ─── Initialize editable values from commercial property API (DB values) ───
  useEffect(() => {
    if (!properties || properties.length === 0) return;
    const cp = properties[0];
    modalFieldChangeHandler('propertyName', { value: cp.name || '' });
    modalFieldChangeHandler('multipleFieldNames', {
      country: { value: cp.country || 'United States' },
      filteredStatesForProperty: { value: getFilteredStatesForAbstraction(cp.country || 'United States') },
    }, 'multiple');
    modalFieldChangeHandler('address', { value: cp.address || '' });
    modalFieldChangeHandler('city', { value: cp.city || '' });
    modalFieldChangeHandler('stateName', { value: cp.state_abbreviation || '' });
    modalFieldChangeHandler('zip', { value: cp.postal_code || '' });
    modalFieldChangeHandler('isWholeBuilding', { value: cp.whole_building != null ? `${cp.whole_building}` : '' });
    modalFieldChangeHandler('propertyType', { value: cp.voucher_property_type_category || '' });
    modalFieldChangeHandler('parcelSize', { value: cp.parcel_size != null ? `${cp.parcel_size}` : '' });
    modalFieldChangeHandler('isListedByCBRE', { value: cp.is_listed != null ? `${cp.is_listed}` : '' });
    if (!cp.whole_building && cp.spaces && cp.spaces.length > 0) {
      modalFieldChangeHandler('suite', { value: cp.spaces[0].address_line2 || '' });
      modalFieldChangeHandler('floorChange', { value: cp.spaces[0].floor || '' });
      modalFieldChangeHandler('squareFeet', {
        value: cp.spaces[0].space_total_square_feet != null ? `${cp.spaces[0].space_total_square_feet}` : '',
      });
    } else if (cp.whole_building) {
      modalFieldChangeHandler('squareFeet', {
        value: cp.building_total_square_feet != null ? `${cp.building_total_square_feet}` : '',
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties && properties[0] && JSON.stringify(properties[0])]);

  // ─── Initialize editable values from tenant company API (DB values) ───
  useEffect(() => {
    if (!company || !company.id) return;

    const shouldSyncTenantCompany = shouldSyncCompanySnapshot(
      tenantCompanySyncRef.current,
      company.id,
      persistedDataRefreshToken,
    );

    if (!shouldSyncTenantCompany) return;

    tenantCompanySyncRef.current = { id: company.id, syncRevision: persistedDataRefreshToken };
    modalFieldChangeHandler('tenantCompanyName', { value: company.name || '' });
    modalFieldChangeHandler('multipleFieldNames', {
      tenantCountryName: { value: company.country || '' },
      filteredStatesForTenant: { value: getFilteredStatesForAbstraction(company.country || 'United States') },
    }, 'multiple');
    modalFieldChangeHandler('tenantAddressLine1', { value: company.address || '' });
    modalFieldChangeHandler('tenantAddressLine2', { value: company.address2 || '' });
    modalFieldChangeHandler('tenantCity', { value: company.city || '' });
    modalFieldChangeHandler('tenantStateName', { value: company.territory || '' });
    modalFieldChangeHandler('tenantZip', { value: company.postal || '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, persistedDataRefreshToken]);

  // ─── Initialize editable values from landlord company API (DB values) ───
  useEffect(() => {
    if (!landlordCompany || !landlordCompany.id) return;

    const shouldSyncLandlordCompany = shouldSyncCompanySnapshot(
      landlordCompanySyncRef.current,
      landlordCompany.id,
      persistedDataRefreshToken,
    );

    if (!shouldSyncLandlordCompany) return;

    landlordCompanySyncRef.current = { id: landlordCompany.id, syncRevision: persistedDataRefreshToken };
    modalFieldChangeHandler('landlordCompanyName', { value: landlordCompany.name || '' });
    modalFieldChangeHandler('multipleFieldNames', {
      landlordCountryName: { value: landlordCompany.country || '' },
      filteredStatesForLandlord: { value: getFilteredStatesForAbstraction(landlordCompany.country || 'United States') },
    }, 'multiple');
    modalFieldChangeHandler('landlordAddressLine1', { value: landlordCompany.address || '' });
    modalFieldChangeHandler('landlordAddressLine2', { value: landlordCompany.address2 || '' });
    modalFieldChangeHandler('landlordCity', { value: landlordCompany.city || '' });
    modalFieldChangeHandler('landlordStateName', { value: landlordCompany.territory || '' });
    modalFieldChangeHandler('landlordZip', { value: landlordCompany.postal || '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landlordCompany?.id, persistedDataRefreshToken]);

  // ─── Initialize editable values from tenant contact API (DB values) ───
  useEffect(() => {
    if (!tenantContact) return;
    modalFieldChangeHandler('tenantPOCFirstName', { value: tenantContact.given_name || '' });
    modalFieldChangeHandler('tenantPOCLastName', { value: tenantContact.surname || '' });
    modalFieldChangeHandler('tenantPOCPhone', { value: tenantContact.phone_numbers?.[0]?.number || '' });
    modalFieldChangeHandler('tenantPOCEmail', { value: tenantContact.email_addresses?.[0]?.address || '' });
    modalFieldChangeHandler('tenantPOCJobTitle', { value: tenantContact.job_title || '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantContact && JSON.stringify(tenantContact)]);

  // ─── Initialize editable values from landlord contact API (DB values) ───
  useEffect(() => {
    if (!landlordContact) return;
    modalFieldChangeHandler('landlordPOCFirstName', { value: landlordContact.given_name || '' });
    modalFieldChangeHandler('landlordPOCLastName', { value: landlordContact.surname || '' });
    modalFieldChangeHandler('landlordPOCPhone', { value: landlordContact.phone_numbers?.[0]?.number || '' });
    modalFieldChangeHandler('landlordPOCEmail', { value: landlordContact.email_addresses?.[0]?.address || '' });
    modalFieldChangeHandler('landlordPOCJobTitle', { value: landlordContact.job_title || '' });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [landlordContact && JSON.stringify(landlordContact)]);


  const handleFieldRefClick = useCallback((label, pagenumber) => {
    if (label !== 'N/A' && pagenumber !== -1) {
      if (label === 'fixedLeaseEscalation') {
        setPdfPageData({
          pageNumber: pagenumber,
          annotationField: `${PARENT_KEYS[modalFieldState.fixedLeaseEscalation?.value?.rent_escalation_is_percent ? 'fixedLeaseEscalationPercent' : label]}_${TRACKING_IDS[modalFieldState.fixedLeaseEscalation?.value?.rent_escalation_is_percent ? 'fixedLeaseEscalationPercent' : label]}`,
        });
      } else {
        setPdfPageData({
          pageNumber: pagenumber,
          annotationField: `${PARENT_KEYS[label]}_${TRACKING_IDS[label]}`,
        });
      }
    } else {
      setPdfPageData({ pageNumber: pagenumber, annotationField: label });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalFieldState.fixedLeaseEscalation]);

  // Property search selection from the abstraction panel — maps EDP/ESRI result onto all
  // property fields and persists to DB in one shot.  Called from lease-information accordion.
  const selectPropertyFromSearch = useCallback((property) => {
    const cp = properties && properties[0];
    if (!cp) return;

    const name = property.name || '';
    const address = property.building.address || '';
    const city = property.building.city || '';
    const postalCode = property.building.postal_code || '';
    const stateAbbr = property.building.state_abbreviation || '';
    const edpId = property.edp_property_id || null;

    // Map ESRI 3-letter country code (e.g. "USA", "CAN") to display name
    const rawCountry = property.building.country || '';
    const countryObj = countries.find(c => c.country_id_A3 === rawCountry)
      || countries.find(c => c.display_name === rawCountry);
    const countryName = countryObj ? countryObj.display_name : 'United States';

    // Bulk-update all property fields in UI state
    modalFieldChangeHandler('multipleFieldNames', {
      propertyName: { value: name },
      address: { value: address },
      city: { value: city },
      country: { value: countryName },
      stateName: { value: stateAbbr },
      filteredStatesForProperty: { value: getFilteredStatesForAbstraction(countryName) },
      zip: { value: postalCode },
    }, 'multiple');

    // Persist all fields + edp_property_id to DB in a single PATCH
    vouchersService.updateCommercialProperty(cp.id, {
      name,
      address,
      city,
      country: countryName,
      state_abbreviation: stateAbbr,
      postal_code: postalCode,
      edp_property_id: edpId,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [properties, modalFieldChangeHandler]);

  const abstractionContextValue = {
    // Marks this as the new enhanced split-pane flow so shared components
    // (leaseAbstractionField, lease-information section, tenant-information, etc.)
    // render their new UI (confidence legend, required asterisks, extraction chip,
    // extra Tenant fields). The old file-modal flow leaves this as the default false.
    isEnhancedFlow: true,
    modalFieldState,
    modalFieldChangeHandler,
    individualModalFieldChangeHandler,
    fieldErrorMap,
    validateLeaseAbstractionFields,
    showPageValidation,
    goToPage: useCallback((pageNumber) => {
      setPdfPageData({ pageNumber: parseInt(pageNumber, 10), annotationField: 'N/A' });
    }, []),
    isMagnified: false,
    setPdfPageData,
    findFieldRef: handleFieldRefClick,
    currentHighlightedField,
    setCurrentHighlightedField,
    // Deal / property direct-save fields (used by lease-information accordion for non-extraction fields)
    deal,
    updateDeal,
    properties,
    onPropertyChange,
    onPropertyBlur,
    onSpaceChange,
    onSpaceBlur,
    onCurrencyBlur,
    voucher,
    updateVoucher,
    agreementTypeData,
    transactionTypeData,
    spaceTypeData,
    buildingClassData,
    // Tenant Information accordion fields
    company,
    onCompanyChange,
    onCompanyBlur,
    tenantSearchState,
    onTenantSuggestionSelect,
    closeTenantSuggestions,
    triggerTenantSearch,
    tenantPartyType,
    isTenantRelocating,
    priorProperty,
    onPriorPropertyChange,
    onPriorPropertyBlur,
    onPriorPropertyCreate,
    onPriorPropertyDelete,
    onPriorSpaceChange,
    onPriorSpaceBlur,
    // Landlord Information accordion fields
    landlordCompany,
    onLandlordCompanyChange,
    onLandlordCompanyBlur,
    landlordSearchState,
    onLandlordSuggestionSelect,
    closeLandlordSuggestions,
    triggerLandlordSearch,
    landlordPartyType,
    // Chip accept → direct DB patch helpers
    patchTenantCompany,
    patchLandlordCompany,
    patchTenantContact,
    patchLandlordContact,
    // Property search — select from EDP/ESRI and auto-fill all property fields
    selectPropertyFromSearch,
  };

  // ─── Nav links for abstraction footer (same logic as BottomNav) ───
  const routerLocation = useLocation();
  const targetLeaseDetailsSection = getTargetLeaseDetailsSection(routerLocation.search);
  const { dealType, isEarlyInvoice } = useSelector(state => ({
    dealType: state.deal.deal?.deal_type,
    isEarlyInvoice: state.voucher?.voucher?.is_early_invoice,
  }));
  const { prev: abstractionPrev, next: abstractionNext } = createLinks(
    dealType, routerLocation.pathname, isEarlyInvoice
  );

  // ─── Lease deal: split layout (abstraction accordions when file present, empty state when not) ───
  if (dealType === 'lease') {    return (
      <LeaseAbstractionContext.Provider value={abstractionContextValue}>
        <div
          className="voucher-lease-abstraction-layout"
          data-testid="voucher-lease-abstraction"
          ref={containerRef}
          style={{ '--lease-abstraction-left-pane-width': getSplitPaneWidthCssValue(leftPaneWidth) }}
        >
          {/* LEFT: instructions banner + 3-accordion form + T&C footer */}
          <div className="voucher-lease-abstraction-layout__left">
            {/* Instructions banner */}
            <div className="lease-abstraction-instructions">
              <div className="lease-abstraction-instructions__row">
                <p>Instructions</p>
                <button
                  type="button"
                  className="lease-abstraction-instructions__show-more"
                  onClick={() => setShowMoreInstructions(!showMoreInstructions)}
                >
                  {showMoreInstructions ? 'Show Less' : 'Show More'}
                </button>
              </div>
              <p className="lease-abstraction-instructions__line1">1. Review the imported data and overwrite fields as needed</p>
              {showMoreInstructions && (
                <div className="lease-abstraction-instructions__extra">
                  <ul>
                    <li>Click page number to navigate to the area of the document the value was found</li>
                    <li>
                      If the system detects a different value, underlined <strong style={{ color: 'var(--t-semantic-info-sem-info-text, #5050BB)' }}>purple text</strong> will
                      appear next to the field. Click to replace the existing value with the value found by the current abstraction.
                    </li>
                  </ul>
                  <p>2. Once you&apos;ve validated the information, check the box at the bottom of the page, confirming you have validated the imported data, then select Continue.</p>
                  <p>3. Repeat the process on each page.</p>
                  <p className="lease-abstraction-instructions__fuzzy-note">* indicates the page number was derived using fuzzy logic and may be inaccurate at times</p>
                </div>
              )}
            </div>
            {/* Confidence legend: always visible */}
            <div className="acc-confidence-legend">
              <div className="acc-confidence-badge acc-confidence-badge--high">
                <span className="acc-confidence-badge__icon" aria-hidden="true" />
                <span>High Confidence</span>
              </div>
              <div className="acc-confidence-badge acc-confidence-badge--low">
                <span className="acc-confidence-badge__icon" aria-hidden="true" />
                <span>Low Confidence</span>
              </div>
              <span className="acc-confidence-legend__different">Different Abstracted Value</span>
              <span className="acc-confidence-legend__matching">Matching Abstracted Value</span>
            </div>
            {/* Lease Information, Tenant Information, Landlord Information accordions */}
            <div className="lease-abstraction-form-scroll">
              <LeaseDetails
                dealId={deal.id}
                isLandlordInformationLoaded={isLandlordInformationLoaded}
                isLeaseInformationLoaded={isLeaseInformationLoaded}
                isTenantInformationLoaded={isTenantInformationLoaded}
                propertyOptions={propertyOptions}
                forceLeaseInformationExpanded={targetLeaseDetailsSection === 'leaseInformation'}
                forceTenantInformationExpanded={targetLeaseDetailsSection === 'tenantInformation'}
                forceLandlordInformationExpanded={targetLeaseDetailsSection === 'landlordInformation'}
                onLeaseInformationExpand={() => loadSection('leaseInformation')}
                onTenantInformationExpand={() => loadSection('tenantInformation')}
                onLandlordInformationExpand={() => loadSection('landlordInformation')}
              />
            </div>
            {/* Footer: T&C checkbox (only when file present) + Back / Continue */}
            <div className="lease-abstraction-footer">
              {activeFile && (
                <div className="lease-abstraction-footer__checkbox">
                  <Checkbox
                    checked={tAndCLeaseDetails}
                    onChange={({ target: { checked } }) => handleLeaseDetailsValidationChange(checked)}
                  >
                    I have validated the data being imported
                  </Checkbox>
                </div>
              )}
              <div className="lease-abstraction-footer__actions">
                {abstractionPrev && (
                  <Link to={abstractionPrev.url} className="cancel-btn">BACK</Link>
                )}
                {abstractionNext && (
                  <Link
                    to={(!activeFile || tAndCLeaseDetails) ? abstractionNext.url : '#'}
                    className={`upload-btn${(activeFile && !tAndCLeaseDetails) ? ' disabled' : ''}`}
                    onClick={e => { if (activeFile && !tAndCLeaseDetails) e.preventDefault(); }}
                  >CONTINUE</Link>
                )}
              </div>
            </div>
            {/* Disclaimer: only when extraction file present */}
            {activeFile && (
            <div className="lease-abstraction-disclaimer">
              <p className="lease-abstraction-disclaimer__title">Disclaimer</p>
              <p>
                The information contained herein includes data that has been extracted using artificial intelligence
                technology. While the technology has been tested for accuracy and reliability, the data in each field
                have not been individually verified for accuracy or reliability. CBRE, Inc. makes no guarantee,
                representation or warranty and accepts no responsibility or liability as to the accuracy, completeness,
                or reliability of the extracted data contained herein. You should conduct a careful, individual
                investigation of all information. Any reliance on this material is solely at your own risk.
              </p>
            </div>
            )}
          </div>
          <div
            className="voucher-lease-abstraction-layout__divider"
            role="separator"
            tabIndex={0}
            aria-label="Resize form and PDF panels"
            aria-orientation="vertical"
            aria-valuemin={minLeftWidth}
            aria-valuemax={maxLeftWidth}
            aria-valuenow={Math.round(leftPaneWidth)}
            onMouseDown={startResize}
            onTouchStart={startResize}
            onKeyDown={handleDividerKeyDown}
            title={dividerHintText}
            style={{ '--split-pane-hint-text': `'${dividerHintText}'` }}
          />
          {/* RIGHT: PDF viewer panel — shows empty/upload state when no file */}
          <div className="voucher-lease-abstraction-layout__right">
            <LeasePdfViewerPanel
              dealFiles={dealFiles || []}
              initialFile={activeFile || null}
              onFileChange={(file) => {
                suppressPersistedRefreshForFileRef.current = file?.id || null;
                setOverrideFile(file);
                try { localStorage.setItem(`abstractionVoucherFile_${deal.id}`, JSON.stringify(file)); } catch (e) { /* ignore */ }
              }}
              onFileUploaded={(file) => {
                suppressPersistedRefreshForFileRef.current = null;
                setOverrideFile(file);
                try { localStorage.setItem(`abstractionVoucherFile_${deal.id}`, JSON.stringify(file)); } catch (e) { /* ignore */ }
              }}
              uploadFile={uploadFile}
              dealId={deal.id}
              extractionResults={extractionResults}
              allExtractionStatusMap={allExtractionStatusMap}
              pdfPageData={pdfPageData}
              setPdfPageData={setPdfPageData}
              setCurrentHighlightedField={setCurrentHighlightedField}
              defaultActiveTab="pdf"
              onFinancialCommentsTabSelected={loadFinancialCommentsPanel}
              persistActiveTab={false}
              tabStorageContext="lease-details"
            />
          </div>
        </div>
      </LeaseAbstractionContext.Provider>
    );
  }

  const manualForm = (
    <div className="lease-information-tab">
      <h2>What New Properties are Being Leased?</h2>
      <form>
        <legend>Details</legend>
        <div className="flex-fields">
          <div className="field-group date-fields">
            <label>
              <span className={`${!deal.date_lease_from && showPageValidation ? 'errorInput' : ''}`}>* </span>
              <span>Lease Start Date</span>
            </label>
            <Datepicker
              date={leaseStartDate}
              onDateChange={date => handleDateChange(date, 'date_lease_from')}
            />
            {(!deal.date_lease_from && showPageValidation) && <span className="date-error">Lease Start Date is required</span>}
          </div>
          <div className="field-group date-fields">
            <label>
              <span className={`${!voucher.lease_expiration_date && showPageValidation ? 'errorInput' : ''}`}>* </span>
              <span>Lease Expiration Date</span>
            </label>
            <Datepicker
              date={leaseEndDate}
              onDateChange={date => handleDateChange(date, 'lease_expiration_date')}
            />
            {(!voucher.lease_expiration_date && showPageValidation) && <span className="date-error">Lease Expiration Date is required</span>}
          </div>
        </div>
        <div className="flex-fields">
          <div className="field-group date-fields">
            <label>
              <span className={`${!deal.date_lease_execution && showPageValidation ? 'errorInput' : ''}`}>* </span>
              <span>Lease Execution Date</span>
            </label>
            <Datepicker
              date={leaseExecutionDate}
              onDateChange={date => handleDateChange(date, 'date_lease_execution')}
            />
            {(!deal.date_lease_execution && showPageValidation) && <span className="date-error">Lease Execution Date is required</span>}
          </div>
          <div className="field-group date-fields">
            <label>
              <span className={`${!deal.date_lease_occupancy && showPageValidation ? 'errorInput' : ''}`}>* </span>
              <span>Lease Occupancy Date</span>
            </label>
            <Datepicker
              date={leaseOccupancyDate}
              onDateChange={date => handleDateChange(date, 'date_lease_occupancy')}
            />
            {(!deal.date_lease_occupancy && showPageValidation) && <span className="date-error">Lease Occupancy Date is required</span>}
          </div>
        </div>
        <div className="field select-subType">
          <label>
            <span className={(agreementType === '' || agreementErrorDrpDwn) ?
            'asterisk-error' : ''}
            >*
            </span> Lease Agreement Type
          </label>
          <Select
            className={deal.lease_agreement_type === '' ? 'errorInput' : ''}
            defaultOption={deal.lease_agreement_type || 'Select'}
            options={agreementTypeData}
            onChange={handleAgreementChange}
          />
          {(agreementErrorDrpDwn) &&
          <p className="asterisk-error top10">Lease Agreement Type is required</p>}
        </div>
        <div className="field select-subtype">
          <label>
            <span className={(transactionType === '' || leaseErrorDrpDwn) ?
            'asterisk-error' : ''}
            >*
            </span> Lease Transaction Type
          </label>
          <Select
            className={deal.lease_income_type === '' ? 'errorInput' : ''}
            defaultOption={deal.lease_income_type || 'Select'}
            options={transactionTypeData}
            onChange={handleLeaseIncomeTypeChange}
          />
          {(leaseErrorDrpDwn) &&
          <p className="asterisk-error top10">Lease Transaction Type is required</p>}
        </div>
        <div>
          <legend className="secondary-legend">Is this an agile/coworking deal?</legend>
          <RadioGroup
            name="coworking-deal"
            className="radioVal"
            selectedValue={`${deal.is_agile_deal}`}
            onChange={handleDealChange}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
        </div>
        {deal.is_agile_deal === 'true' &&
        <div>
          <div className="field">
            <label>Number of Seats</label>
            <input
              type="number"
              name="number-of-seats"
              value={deal.number_of_seats || noOfSeats || ''}
              onChange={handleNumseatChange}
            />
          </div>
          <div className="field">
            <label>Cost per Seat($)</label>
            <input
              type="number"
              name="cost-per-seat"
              value={deal.cost_per_seat || costPerSeat || ''}
              onChange={handleSeatChange}
              onBlur={handleSeatChangeBlur}
            />
          </div>
        </div>}
        <div>
          <legend className="secondary-legend">Is this a sublease?</legend>
          <RadioGroup
            name="sublease-deal"
            className="radioVal"
            selectedValue={`${deal.is_sublease}`}
            onChange={handleLeaseChange}
            orientation="vertical"
          >
            <RadioButton value="false">No</RadioButton>
            <RadioButton value="true">Yes</RadioButton>
          </RadioGroup>
        </div>
        <div>
          {properties.map(cp => (
            <div key={cp.id}>
              <div>
                <legend>Property Being Leased</legend>
                <div>
                  <ul className="property-list">
                    {dealProperties && dealProperties.filter(dp => linkedProperties[cp.id] !== dp.id).map((dp, i) => ((i >= 2 && !showMore[cp.id]) ? null :
                  (
                    <li className="property-list-style" key={dp.id} onClick={() => handleLinkedProperty(cp.id, dp)}>
                      <div className="person-component">
                        <div className="person-textbox">
                          <h4>{dp.name}</h4>
                          <p>{dp.building.full_address}</p>
                        </div>
                      </div>
                      <div>
                        <span className="add-icon">
                          <Icon iconName="plus-circle-outline" size="large" />
                        </span>
                      </div>
                    </li>
                  )))
                }
                  </ul>
                </div>
                <span>
                  {dealProperties.length > (linkedProperties[cp.id] ? 3 : 2) &&
                  <React.Fragment>
                    <span className="poc-text">
                      {showMore[cp.id] ?
                        <span className="show-more" onClick={() => handleShowMore(cp.id)}>
                          <Icon iconName="minus-square icon-square" size="small" /> <span className="show-text">Show Less</span>
                        </span> :
                        <span className="show-more" onClick={() => handleShowMore(cp.id)}>
                          <Icon iconName="plus-square icon-square" size="small" />
                          <span className="show-text">Show More</span> ({dealProperties.length - (linkedProperties[cp.id] ? 3 : 2)})
                        </span>}
                    </span>
                  </React.Fragment>}
                </span>
              </div>
              <div className="div-align">
                <label>Property Search Tip
                  <Tooltip
                    message={
                      <>
                        <h3>Property Search Tip</h3>
                        <br />
                        <p>Try entering a street address, intersection, and include the city name.</p>
                      </>
                    }
                  >
                    <Icon iconName="info-circle" />
                  </Tooltip>
                </label>
                <PropertyAutocomplete
                  errorMessage=""
                  onSelect={property => addProperty(cp.id, property)}
                />
              </div>
              <div className="field">
                <label>Property Name</label>
                <input
                  type="text"
                  name="name"
                  value={cp.name || ''}
                  onChange={onPropertyChange(cp.id)}
                />
              </div>
              <AddressForm
                address={cp.address || ''}
                city={cp.city || ''}
                country={cp.country || ''}
                postal={cp.postal_code || ''}
                territory={cp.state_abbreviation || ''}
                onChange={e => onRemapPropertyEvent(e, onPropertyChange(cp.id))}
                onBlur={e => onRemapPropertyEvent(e, onPropertyBlur(cp.id))}
                isRelocating
                showValidation={showPageValidation}
              />
              <div>
                <legend className="secondary-legend">Is Property Confidential?</legend>
                <RadioGroup
                  name="is_property_confidential"
                  className="radioVal"
                  selectedValue={`${cp.is_property_confidential}`}
                  onChange={onPropertyChange(cp.id)}
                  orientation="vertical"
                >
                  <RadioButton value="false">No</RadioButton>
                  <RadioButton value="true">Yes</RadioButton>
                </RadioGroup>
              </div>
              <div>
                <legend className="secondary-legend">Is this the whole building, or suites in a building?</legend>
                <RadioGroup
                  name="whole_building"
                  className="radioVal"
                  selectedValue={`${cp.whole_building}`}
                  onChange={onPropertyChange(cp.id)}
                  orientation="vertical"
                >
                  <RadioButton value="true">Whole Building</RadioButton>
                  <RadioButton value="false">Suite(s)</RadioButton>
                </RadioGroup>
              </div>
              <div className="field select-subType sub-type">
                <label>
                  <span className={(cp.voucher_property_type_category === '' && showPageValidation) ?
                  'asterisk-error' : ''}
                  >*
                  </span> Property Type / Subtype
                </label>
                <Select
                  className={cp.voucher_property_type_category === '' ? 'errorInput' : ''}
                  defaultOption={cp.voucher_property_type_category || 'Select'}
                  options={propertyOptions.filter(opt => opt.value !== '')}
                  onChange={e => onPropertyChange(cp.id)({
                  target: {
                    type: 'select',
                    name: 'voucher_property_type_category',
                    value: e.value === null ? '' : e.value,
                  },
                })}
                />
                {(cp.voucher_property_type_category === '' && showPageValidation) &&
                <p className="asterisk-error top10">Property Type / Subtype is required</p>}
              </div>
              <div className="field select-subType">
                <label>
                  <span className={(cp.space_type === '' && showPageValidation) ?
                  'asterisk-error' : ''}
                  >*
                  </span> Space Type
                </label>
                <Select
                  className={cp.space_type === '' ? 'errorInput' : ''}
                  defaultOption={cp.space_type || 'Select'}
                  options={spaceTypeData.filter(opt => opt.value !== '')}
                  onChange={e => onPropertyChange(cp.id)({
                  target: {
                    type: 'select',
                    name: 'space_type',
                    value: e.value === null ? '' : e.value,
                  },
                })}
                />
                {(cp.space_type === '' && showPageValidation) &&
                <p className="asterisk-error top10">Space Type is required</p>}
              </div>
              {cp.whole_building === true &&
              <div className="field">
                <label>
                  <span className={(cp.building_total_square_feet === null && showPageValidation) ?
                  'asterisk-error' : ''}
                  >*
                  </span>Square Footage
                </label>
                <NumberInput
                  name="building_total_square_feet"
                  value={cp.building_total_square_feet !== null ? parseFloat(cp.building_total_square_feet.toString()) : null}
                  onBlur={onCurrencyBlur(cp.id, 'building_total_square_feet')}
                />
                {((cp.building_total_square_feet === null || cp.building_total_square_feet === '') && showPageValidation) &&
                <p className="asterisk-error square-foot">Square Footage is required</p>}
              </div>}
              <div className="field">
                <label>Parcel Size/Acres</label>
                <NumberInput
                  name="parcel_size"
                  value={cp.parcel_size !== null ? parseFloat(cp.parcel_size.toString()) : null}
                  onBlur={onCurrencyBlur(cp.id, 'parcel_size')}
                />
              </div>
              <div>
                <legend className="secondary-legend">Is Property listed by CBRE?</legend>
                <RadioGroup
                  name="is_listed"
                  className="radioVal"
                  selectedValue={`${cp.is_listed}`}
                  onChange={onPropertyChange(cp.id)}
                  orientation="vertical"
                >
                  <RadioButton value="false">No</RadioButton>
                  <RadioButton value="true">Yes</RadioButton>
                </RadioGroup>
              </div>
              {cp.is_listed === true &&
              <div className="field">
                <label>CBRE Listing #</label>
                <input
                  type="text"
                  name="cbre_listing_number"
                  value={cp.cbre_listing_number || ''}
                  onChange={onPropertyChange(cp.id)}
                  onBlur={onPropertyBlur(cp.id)}
                />
              </div>}
              <div>
                <legend className="secondary-legend">Is Property Managed by CBRE?</legend>
                <RadioGroup
                  name="is_property_managed_by_cbre"
                  className="radioVal"
                  selectedValue={`${cp.is_property_managed_by_cbre}`}
                  onChange={onPropertyChange(cp.id)}
                  orientation="vertical"
                >
                  <RadioButton value="false">No</RadioButton>
                  <RadioButton value="true">Yes</RadioButton>
                </RadioGroup>
              </div>
              <div className="field">
                <label>Mix Property ID</label>
                <input
                  type="text"
                  name="mix_property_id"
                  value={cp.mix_property_id || ''}
                  onChange={onPropertyChange(cp.id)}
                  onBlur={onPropertyBlur(cp.id)}
                />
              </div>
              <div className="field select-subType">
                <label>
                  Building Class
                </label>
                <Select
                  defaultOption={cp.building_class || 'Select'}
                  options={buildingClassData.filter(opt => opt.value !== '')}
                  onChange={e => onPropertyChange(cp.id)({
                  target: {
                    type: 'select',
                    name: 'building_class',
                    value: e.value === null ? '' : e.value,
                  },
                })}
                />
              </div>
              <div className="field">
                <label>Portfolio Name</label>
                <input
                  type="text"
                  name="portfolio_name"
                  value={cp.portfolio_name || ''}
                  onChange={onPropertyChange(cp.id)}
                  onBlur={onPropertyBlur(cp.id)}
                />
              </div>
              <div>
                {!cp.whole_building && cp.spaces?.map((space, i) => (
              // eslint-disable-next-line react/no-array-index-key
                  <div key={`${cp.id}-${i}`}>
                    <legend>Suite
                    </legend>
                    <div className="form-row">
                      <div className="field-group col-3">
                        <label>Suite</label>
                        <input
                          type="text"
                          name="address_line2"
                          value={space.address_line2 || ''}
                          onChange={onSpaceChange(cp.id, i)}
                          onBlur={onSpaceBlur(cp.id, i)}
                        />
                      </div>
                      <div className="field-group col-3">
                        <label>Floor</label>
                        <input
                          type="text"
                          name="floor"
                          value={space.floor || ''}
                          onChange={onSpaceChange(cp.id, i)}
                          onBlur={onSpaceBlur(cp.id, i)}
                        />
                      </div>
                      <div className={space.space_total_square_feet === null || space.space_total_square_feet === ''
                          ? 'field-group col-3 top10' : 'field-group col-3'}
                      >
                        <label>
                          <span className={((space.space_total_square_feet === null || space.space_total_square_feet === '') && showPageValidation)
                            ? 'asterisk-error' : ''}
                          >*
                          </span>
                          <span> Square Feet</span>
                        </label>
                        <input
                          className={(space.space_total_square_feet === null || space.space_total_square_feet === '') ? 'errorInput' : ''}
                          type="number"
                          name="space_total_square_feet"
                          value={space.space_total_square_feet || ''}
                          onChange={onSpaceChange(cp.id, i)}
                          onBlur={onSpaceBlur(cp.id, i)}
                        />
                        {((space.space_total_square_feet === null || space.space_total_square_feet === '') && showPageValidation) &&
                        <p className="asterisk-error">Square Feet is required</p>}
                      </div>
                    </div>
                  </div>
            ))
            }
              </div>
            </div>
        ))}
        </div>
      </form>
    </div>
  );

  return manualForm;
};

LeaseInformation.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  updateDeal: PropTypes.func.isRequired,
  updateVoucher: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  abstractionLeaseFile: PropTypes.object,
  dealFiles: PropTypes.arrayOf(PropTypes.object),
  fetchFiles: PropTypes.func,
  fetchFinancials: PropTypes.func,
  uploadFile: PropTypes.func,
  voucher: PropTypes.shape({
    id: PropTypes.number.isRequired,
    lease_expiration_date: PropTypes.string,
  }).isRequired,
  deal: PropTypes.shape({
    rep_role: PropTypes.string,
    whole_building: PropTypes.string,
    date_lease_from: PropTypes.string,
    date_lease_execution: PropTypes.string,
    date_lease_occupancy: PropTypes.string,
    lease_agreement_type: PropTypes.string,
    lease_income_type: PropTypes.string,
    number_of_seats: PropTypes.string,
    date_lease_to: PropTypes.string,
    deal_type: PropTypes.string,
    is_agile_deal: PropTypes.bool,
    is_dual_rep_deal: PropTypes.bool,
    id: PropTypes.number,
    is_sublease: PropTypes.bool,
    cost_per_seat: PropTypes.string,
    client: PropTypes.shape({
      id: PropTypes.number,
    }),
  }).isRequired,
};

LeaseInformation.defaultProps = {
  location: '',
  abstractionLeaseFile: null,
  dealFiles: [],
  fetchFiles: () => {},
  fetchFinancials: () => {},
  uploadFile: () => {},
};

export default LeaseInformation;
